import { SearchIcon } from '@chakra-ui/icons';
import { matchesNameQuery } from '../../../utils/name-search';
import {
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import FormModal from '../../../components/FormModal';
import { useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { useGetUsers } from '../../user-management/hooks/useGetUsers';
import { useAddUsersToCohort } from '../hooks/useAddUsersToCohort';

type Props = {
  cohortId: string;
  isOpen: boolean;
  onClose: () => void;
};

export default function AddUsersToCohortModal({
  cohortId,
  isOpen,
  onClose,
}: Props) {
  const { selectedOrganization } = useSelectedOrganization();
  // Gated on `isOpen` — CohortDetailPage mounts this modal unconditionally, so
  // viewing any cohort pulled the whole roster (DASHBOARD-Z).
  // R92-5 — `isError` is taken as well as `isLoading`. Post-R89-1 a refused
  // `GET /users` leaves `data === undefined`, `filteredUsers` falls to `[]`,
  // and the empty state below could only choose between "loading" and "no users
  // found" — so an operator who opened this modal to add members to a cohort
  // was told their organization has nobody in it.
  //
  // R118-4 — and `isLoading` was the RAW flag, which is the defect one layer
  // under the one the comment above describes (D72: R92-5 closed the second
  // term of a two-term gate and left the first). `useGetUsers` is `enabled:
  // Boolean(organizationId) && (options?.enabled ?? true)` and this call site
  // supplies only `enabled: isOpen`; `CohortDetailPage` is gated on `cohortId`
  // from the route and reads no organization at all. React Query v4 reports a
  // DISABLED query as `isLoading: true` with `fetchStatus: 'idle'`, so with
  // nothing selected this modal showed a PERMANENT "Loading…" over a roster
  // nobody had asked for. `isInitialLoading` is false for a disabled query.
  const { data: users, isInitialLoading: usersLoading, isError: isUsersError } = useGetUsers(
    selectedOrganization?.id,
    undefined,
    { enabled: isOpen },
  );
  const usersReadState = classifyRead(isUsersError, usersLoading, users);
  const { mutateAsync: addUsers, isLoading } = useAddUsersToCohort(cohortId);
  const toast = useToast();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUsers = useMemo(() => {
    if (!users) return [];
    if (!searchQuery) return users;

    return users.filter((user) => matchesNameQuery(searchQuery, user));
  }, [users, searchQuery]);

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleToggleAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map((u) => u.id));
    }
  };

  const handleSubmit = async () => {
    if (selectedUserIds.length === 0) {
      toast({
        title: translate('cohort.no_users_selected'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const result = await addUsers({ userIds: selectedUserIds });

      toast({
        title: translate('cohort.users_added_successfully'),
        description: `${result.added} ${translate('cohort.added')}, ${result.skipped} ${translate('cohort.skipped')}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setSelectedUserIds([]);
      setSearchQuery('');
      onClose();
    } catch (error) {
      toast({
        title: translate('cohort.add_users_failed'),
        description: error instanceof Error ? error.message : undefined,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const onModalClose = () => {
    setSelectedUserIds([]);
    setSearchQuery('');
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onModalClose}
      title={translate('cohort.add_users_to_cohort')}
      size="xl"
      onSubmit={handleSubmit}
      submitLabel={translate('cohort.add_selected_users')}
      cancelLabel={translate('common.cancel')}
      isSubmitting={isLoading}
      isSubmitDisabled={selectedUserIds.length === 0}
    >
      <FormControl mb={4}>
        <FormLabel>{translate('cohort.search_users')}</FormLabel>
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={translate('cohort.search_by_name_or_email')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>
      </FormControl>

      <FormControl>
        <Checkbox
          isChecked={
            filteredUsers.length > 0 &&
            selectedUserIds.length === filteredUsers.length
          }
          onChange={handleToggleAll}
          mb={2}
        >
          <Text fontWeight="bold">
            {translate('cohort.select_all')} ({filteredUsers.length})
          </Text>
        </Checkbox>
      </FormControl>

      <Stack spacing={2} maxH="400px" overflowY="auto" mt={4}>
        {filteredUsers.map((user) => (
          <Checkbox
            key={user.id}
            isChecked={selectedUserIds.includes(user.id)}
            onChange={() => handleToggleUser(user.id)}
          >
            <Text>
              {user.firstName} {user.lastName} ({user.email})
            </Text>
          </Checkbox>
        ))}

        {filteredUsers.length === 0 && (
          <Text
            color={usersReadState === 'unreadable' ? 'danger.500' : 'gray.500'}
            textAlign="center"
            py={8}
          >
            {usersReadState === 'loading'
              ? translate('cohort.loading')
              : usersReadState === 'unreadable'
                ? translate('user.error_loading')
                : usersReadState === 'never-issued'
                  // Borrowed key (R96-7 freeze); a text line rather than a
                  // `ScopePrompt` panel — one control inside a form.
                  ? translate(scopePromptI18nKey('organization') as any)
                  : translate('cohort.no_users_found')}
          </Text>
        )}
      </Stack>

      <Text mt={4} color="gray.600" fontSize="sm">
        {selectedUserIds.length} {translate('cohort.users_selected')}
      </Text>
    </FormModal>
  );
}
